import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { useParams, useLocation } from "wouter"
import { ArrowRight, FileInput, BarChart3, CheckCircle2, Target, FileText, TrendingUp, Users} from "lucide-react"
import { useValueAssessment } from "@/contexts/ValueAssessmentContext"
import { useEffect } from "react"

export default function IntroScreen() {
  const params = useParams()
  const [_location, navigate] = useLocation()
  
  const projectId = params.projectId
  const { data } = useValueAssessment()

  const pricingEnabled = data.valueSummary?.pricingEnabled || false

  useEffect(() => {
    console.log("✅ Intro screen rendered. Pricing enabled:", pricingEnabled)
  }, [pricingEnabled])

  const handleStartAssessment = () => {
    navigate(`/value-assessment/${projectId}/kickoff`)
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-slate-800 dark:text-white ">Value Assessment</h1>
        <p className="text-lg text-gray-300 max-w-2xl mx-auto leading-relaxed">
          Assess your Record-to-Report automation potential and build a data-driven business case
        </p>

      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* What You'll Provide */}
        <Card className="dark:bg-slate-700 border-[#334155] h-fit">
          <CardHeader className="pb-4">
            <CardTitle className="text-slate-800 dark:text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <FileInput className="w-5 h-5 text-primary" />
              </div>
              What You'll Provide
            </CardTitle>
            <p className="text-gray-400 text-sm">Your inputs (estimates are perfectly fine)</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-slate-800 dark:text-white font-medium">FTE Allocation</h4>
                <p className="text-slate-600 dark:text-gray-300 text-sm">Current team distribution across R2R activities</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-slate-800 dark:text-white font-medium">Technology Landscape</h4>
                <p className="text-slate-600 dark:text-gray-300 text-sm">ERP systems, tools, and current capabilities</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-slate-800 dark:text-white font-medium">Strategic Context</h4>
                <p className="text-slate-600 dark:text-gray-300 text-sm">Goals, pain points, and implementation blockers</p>
              </div>
            </div>
            

          </CardContent>
        </Card>

        {/* What You'll Receive */}
        <Card className="dark:bg-slate-700 border-[#334155] h-fit">
          <CardHeader className="pb-4">
            <CardTitle className="text-white flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-primary" />
              </div>
              What You'll Receive
            </CardTitle>
            <p className="text-gray-400 text-sm">Professional analysis and recommendations</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <Target className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-slate-800 dark:text-white font-medium">Automation Potential</h4>
                <p className="text-slate-600 dark:text-gray-300 text-sm">FTE optimization opportunities by region and activity</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-slate-800 dark:text-white font-medium">Executive Summary</h4>
                <p className="text-slate-600 dark:text-gray-300 text-sm">Professional presentation and detailed report</p>
              </div>
            </div>
            
            {pricingEnabled ? (
              <div className="flex items-start gap-3">
                <TrendingUp className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-slate-800 dark:text-white font-medium">ROI Analysis</h4>
                  <p className="text-slate-600 dark:text-gray-300 text-sm">Investment modeling and payback calculations</p>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <Users className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="text-slate-800 dark:text-white font-medium">Value Analysis</h4>
                  <p className="text-slate-600 dark:text-gray-300 text-sm">FTE optimization and productivity opportunities</p>
                </div>
              </div>
            )}
            

          </CardContent>
        </Card>
      </div>

      {/* Process Overview */}
      <Card className="dark:bg-[#0F172A] bg-slate-800 border-[#334155]">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center gap-8 text-center">
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary text-black flex items-center justify-center font-semibold text-sm">
                1
              </div>
              <p className="text-gray-300 text-sm">Project Setup</p>
            </div>
            <div className="w-8 h-px bg-[#334155]"></div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#334155] text-gray-300 flex items-center justify-center font-semibold text-sm">
                2
              </div>
              <p className="text-gray-300 text-sm">FTE Allocation</p>
            </div>
            <div className="w-8 h-px bg-[#334155]"></div>
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#334155] text-gray-300 flex items-center justify-center font-semibold text-sm">
                3
              </div>
              <p className="text-gray-300 text-sm">Value Summary</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <div className="text-center space-y-4">
        <Button 
          onClick={handleStartAssessment}
          size="lg"
          className="bg-primary hover:bg-primaryHover text-forestBlack font-semibold px-8 py-4 text-lg"
          data-cy="StartValueAssessment"
        >
          Start Value Assessment
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>


      </div>
    </div>
  )
}